import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api, { API } from "@/utils/api";
import DelhiveryTransitModal from "@/components/DelhiveryTransitModal";
import ShipmentEditModal, { canEditShipment } from "@/components/ShipmentEditModal";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, Loader2, Plus, Download, Eye, Truck, Package, FileText, Upload,
} from "lucide-react";

const SOURCE_BADGE = {
  OWN: "bg-emerald-100 text-emerald-700 border-emerald-200",
  DELHIVERY_API: "bg-blue-100 text-blue-700 border-blue-200",
  MANUAL: "bg-slate-100 text-slate-600 border-slate-200",
  SYSTEM: "bg-purple-100 text-purple-700 border-purple-200",
  API: "bg-blue-100 text-blue-700 border-blue-200",
};

const EVENT_TYPES = [
  "ORDER_CREATED", "MANIFEST_CREATED", "PICKUP_SCHEDULED",
  "PICKUP_ATTEMPTED", "PICKED_UP", "FM_IN_TRANSIT",
  "ARRIVED_ORIGIN_POINT", "LOADED_TO_VEHICLE", "VEHICLE_DEPARTED",
  "IN_TRANSIT", "ARRIVED_DEST_POINT",
  "RECEIVED_BY_LM_PARTNER", "OUT_FOR_DELIVERY", "DELIVERY_ATTEMPTED",
  "DELIVERED", "POD_UPLOADED",
  "NDR_RAISED", "ADDRESS_ISSUE", "CUSTOMER_NOT_AVAILABLE", "DAMAGED",
  "LOST", "UNEXPECTED_SCAN", "DELAYED", "RTO_INITIATED",
  "3PL_MANIFESTED", "3PL_IN_TRANSIT", "3PL_OUT_FOR_DELIVERY",
  "3PL_DELIVERED", "3PL_NDR",
];
const MIDMILE = new Set(["VEHICLE_DEPARTED", "LOADED_TO_VEHICLE", "IN_TRANSIT", "ARRIVED_DEST_POINT", "ARRIVED_ORIGIN_POINT"]);

const fmtType = (t) =>
  (t || "")
    .replace(/^3PL_/, "Courier ")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

export default function ShipmentTracking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shipment, setShipment] = useState(null);
  const [customerProfile, setCustomerProfile] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [liveStatus, setLiveStatus] = useState(null);

  // Manual scan form
  const [form, setForm] = useState({
    event_type: "PICKED_UP",
    event_date: "",
    display_message: "",
    internal_message: "",
    display_location: "",
    vehicle_number: "",
    attempt_number: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [showTransit, setShowTransit] = useState(false);
  const [pickupRequest, setPickupRequest] = useState(null);
  const [editHeaderOpen, setEditHeaderOpen] = useState(false);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [pickupLoc, setPickupLoc] = useState(null);
  const [showPickupCompare, setShowPickupCompare] = useState(false);
  const [addrDetail, setAddrDetail] = useState(null);
  const [showAllAwbs, setShowAllAwbs] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const sh = await api.get(`/shipments/${id}`).catch(() => ({ data: null }));
      setShipment(sh.data);
      if (sh.data?.customer_id) {
        const prof = await api.get(`/v2/customers/profile/${sh.data.customer_id}`).catch(() => ({ data: null }));
        setCustomerProfile(prof.data);
      }
      // Fetch the real Delhivery PUR number (not just our internal PKR
      // code) for the Reference Numbers panel -- lives on the separate
      // pickup_requests record, not on the shipment itself.
      if (sh.data?.pickup_request_id) {
        const pur = await api.get(`/v2/pickup-requests/${sh.data.pickup_request_id}`).catch(() => ({ data: null }));
        setPickupRequest(pur.data);
      } else {
        setPickupRequest(null);
      }
      // B2B shipments never set awb/awb_number/tracking_id -- their real
      // identifier is delhivery_lrnum. Falling back to it here fixes B2B
      // tracking pages showing "Shipment has no AWB" and never even
      // attempting the real tracking API call (Aug 19, 2026).
      const awb = sh.data?.tracking_id || sh.data?.awb || sh.data?.awb_number || sh.data?.delhivery_lrnum;
      if (!awb) {
        toast.error("Shipment has no AWB");
        return;
      }
      const r = await api.get(`/v2/tracking/${awb}/full`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load tracking");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const is3pl = ["DELHIVERY_B2B", "DELHIVERY_B2C", "DELHIVERY_C2C"].includes(
    (shipment?.assigned_carrier || shipment?.network_type || "").toUpperCase()
  );
  const awb = is3pl
    ? (shipment?.awb || shipment?.awb_number || data?.awb || shipment?.tracking_id)
    : (shipment?.own_network_lr || shipment?.lr_number || shipment?.awb_number || shipment?.tracking_id);
  const lr = shipment?.delhivery_lrnum || shipment?.own_network_lr || null;

  const events = data?.events || [];
  const lastSync = [...events].reverse().find((e) => e.source === "DELHIVERY_API")?.event_date;

  const submitEvent = async () => {
    if (!form.event_type) return;
    setSubmitting(true);
    try {
      const payload = {
        event_type: form.event_type,
        display_message: form.display_message || undefined,
        internal_message: form.internal_message || undefined,
        display_location: form.display_location || undefined,
        vehicle_number: form.vehicle_number || undefined,
        attempt_number: form.attempt_number ? Number(form.attempt_number) : undefined,
        event_date: form.event_date || undefined,
      };
      await api.post(`/v2/tracking/${awb}/event`, payload);
      toast.success("Event added");
      setScanOpen(false);
      setForm({ ...form, display_message: "", internal_message: "", display_location: "", vehicle_number: "", attempt_number: "" });
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const sync3PL = async () => {
    if (!lr) return;
    setSyncing(true);
    try {
      const r = await api.post(`/v2/tracking/sync-3pl/${lr}`);
      const d = r.data;
      toast.success(`Synced ${d.synced_count} new events (${d.skipped_count} skipped)`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const checkLive = async () => {
    try {
      const r = await api.get(`/v2/tracking/3pl/status/${awb}`);
      setLiveStatus(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const downloadLabel = async (singleAwb) => {
    try {
      const token = localStorage.getItem("sendtoo_token");
      const url = singleAwb ? `${API}/v2/labels/${id}/${singleAwb}` : `${API}/v2/labels/${id}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = `Sendtoo_Label_${singleAwb || lr || id}.pdf`;
      a.click();
      URL.revokeObjectURL(u);
    } catch {
      toast.error("Label download failed");
    }
  };

  useEffect(() => {
    const sid = shipment?.id || shipment?._id;
    if (!sid || !shipment?.warehouse) { setPickupLoc(null); return; }
    api.get(`/v2/shipment-edit/${sid}`).then((r) => setPickupLoc(r.data?.pickup_location || null)).catch(() => setPickupLoc(null));
  }, [shipment?.id, shipment?._id, shipment?.warehouse]);
  const sortedEvents = [...events].sort((x, y) => new Date(y.event_date || 0) - new Date(x.event_date || 0));
  const awbs = shipment?.awb_numbers || shipment?.awbs || (awb ? [awb] : []);

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="shipment-tracking-page">
        <button
          onClick={() => navigate("/admin/shipments")}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"
          data-testid="back-to-shipments"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Shipments
        </button>

        {editHeaderOpen && <ShipmentEditModal shipmentId={id} onClose={() => setEditHeaderOpen(false)} onSaved={loadAll} />}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Tracking — {awb || "—"}</h1>
            {canEditShipment(shipment) && <button onClick={() => setEditHeaderOpen(true)} data-testid="edit-shipment-header-btn" className="mt-1 px-3 py-1 text-xs font-semibold border border-slate-300 rounded-lg hover:bg-slate-50">Edit shipment details</button>}
            <p className="text-sm text-slate-500">LR: {lr || "—"}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadAll} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-xs font-bold text-slate-500 uppercase mb-2">Pickup Location</div>
            <div className="font-semibold text-slate-800">{shipment?.origin?.name}</div>
            <div className="text-sm text-slate-600 mt-1">{shipment?.origin?.address}</div>
            <div className="text-sm text-slate-600">
              {shipment?.origin?.city}, {shipment?.origin?.state} — {shipment?.origin?.pincode}
            </div>
            <div className="text-sm text-slate-500 mt-1">{shipment?.origin?.phone}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-xs font-bold text-slate-500 uppercase mb-2">Drop Location</div>
            <div className="font-semibold text-slate-800">{shipment?.destination?.name}</div>
            <div className="text-sm text-slate-600 mt-1">{shipment?.destination?.address}</div>
            <div className="text-sm text-slate-600">
              {shipment?.destination?.city}, {shipment?.destination?.state} — {shipment?.destination?.pincode}
            </div>
            <div className="text-sm text-slate-500 mt-1">{shipment?.destination?.phone}</div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs font-bold text-slate-500 uppercase mb-3">Reference Numbers</div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-x-4 gap-y-3 items-start">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm">📦</span>
              <div>
                <div className="text-[10px] uppercase font-bold text-slate-400">Order ID</div>
                <div className="font-mono font-semibold text-slate-800 text-sm">{shipment?.order_id || "—"}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-sm">🌐</span>
              <div>
                <div className="text-[10px] uppercase font-bold text-slate-400">Network</div>
                <div className="font-semibold text-slate-800 text-sm">
                  {(shipment?.connection_through === "SENDTOO_NETWORK" || shipment?.assigned_carrier === "OWN_NETWORK")
                    ? "Sendtoo Network"
                    : (shipment?.assigned_carrier || shipment?.connection_through || "—").replace(/_/g, " ")}
                </div>
              </div>
            </div>
            {shipment?.pickup_request_id && (
              <div className="flex items-center gap-3 col-span-2">
                <span className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center text-sm">🚚</span>
                <div>
                  <div className="text-[10px] uppercase font-bold text-slate-400">Pickup Request (PUR)</div>
                  <div className="font-mono font-semibold text-slate-800 text-sm">
                    {shipment.pickup_request_id}
                    {pickupRequest?.delhivery_pur_number && (
                      <span className="text-slate-400 font-normal"> · Delhivery: {pickupRequest.delhivery_pur_number}</span>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center text-sm">⚡</span>
              <div>
                <div className="text-[10px] uppercase font-bold text-slate-400">Service Level</div>
                <div className="font-semibold text-slate-800 text-sm">
                  {shipment?.service_level === "NDD" ? "Next Day Delivery"
                    : shipment?.service_level === "EXPRESS" ? "Express"
                    : shipment?.service_level === "STANDARD" ? "Standard"
                    : shipment?.service_level || "—"}
                </div>
              </div>
            </div>
            {lr && (
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center text-sm">🧾</span>
                <div>
                  <div className="text-[10px] uppercase font-bold text-slate-400">LR Number</div>
                  <div className="font-mono font-semibold text-slate-800 text-sm">{lr}</div>
                </div>
              </div>
            )}
            <div className="flex items-start gap-3 col-span-full border-t border-slate-100 pt-3">
              <span className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center text-sm flex-shrink-0">🏷️</span>
              <div>
                <div className="text-[10px] uppercase font-bold text-slate-400 mb-1">
                  {shipment?.box_count > 1 ? `AWB Numbers (${shipment.box_count} boxes)` : "AWB"}
                </div>
                {shipment?.is_multibox && Array.isArray(shipment?.awb_numbers) && shipment.awb_numbers.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-1.5">
                    {shipment.awb_numbers.slice(0, 2).map((a, i) => (
                      <span key={i} className="font-mono text-xs bg-slate-100 px-2 py-1 rounded border border-slate-200">Box {i + 1}: {a}</span>
                    ))}
                    {shipment.awb_numbers.length > 2 && (
                      <button onClick={() => setShowAllAwbs(true)} data-testid="view-all-awbs" className="text-xs font-semibold text-brand-600 border border-brand-200 rounded px-2 py-1 hover:bg-brand-50">
                        View all {shipment.awb_numbers.length} AWBs
                      </button>
                    )}
                  </div>
                ) : is3pl ? (
                  <button
                    onClick={() => setShowTransit(true)}
                    className="font-mono text-sm font-semibold text-indigo-600 hover:underline"
                    data-testid="awb-link-tracking-page"
                  >
                    {awb || "—"}
                  </button>
                ) : (
                  <span className="font-mono text-sm font-semibold">{awb || "—"}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs font-bold text-slate-500 uppercase mb-3">Package Details</div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-slate-400 text-xs">Weight (Declared)</div>
              <div className="font-semibold">{shipment?.package?.weight ?? shipment?.chargeable_weight} kg</div>
            </div>
            {shipment?.b2b_weight_reconciled && (
              <div>
                <div className="text-slate-400 text-xs">Weight (Post-Reconciliation)</div>
                <div className="font-semibold text-purple-600">
                  {shipment?.chargeable_weight} kg
                  <span className="text-slate-400 font-normal text-xs ml-1">
                    (was {shipment?.b2b_weight_pre_reconciliation} kg)
                  </span>
                </div>
              </div>
            )}
            <div>
              <div className="text-slate-400 text-xs">Number of Boxes</div>
              <div className="font-semibold">{shipment?.box_count || 1}</div>
            </div>
            <div>
              <div className="text-slate-400 text-xs">Amount {shipment?.bill_amount_override != null ? "(Reconciled)" : "(Original)"}</div>
              <div className={`font-semibold ${shipment?.bill_amount_override != null ? "text-purple-600" : ""}`}>
                ₹{shipment?.bill_amount_override ?? shipment?.price}
              </div>
            </div>
          </div>
        </div>
       

        <div>
          <div className="text-xs font-bold text-slate-500 uppercase mb-2">Addresses</div>
          {(() => {
            const custAddr = customerProfile?.business_address?.trim();
            const originStr = shipment?.origin
              ? `${shipment.origin.address || ""}, ${shipment.origin.city || ""} - ${shipment.origin.pincode || ""}`.trim()
              : "";
            const destStr = shipment?.destination
              ? `${shipment.destination.address || ""}, ${shipment.destination.city || ""} - ${shipment.destination.pincode || ""}`.trim()
              : "";
            const originMatchesCustomer = custAddr && originStr &&
              custAddr.toLowerCase().replace(/\s+/g, "") === originStr.toLowerCase().replace(/\s+/g, "");
            return (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div role="button" tabIndex={0} onClick={() => setAddrDetail("customer")} data-testid="addr-card-customer"
                  className="bg-white border border-slate-200 rounded-xl p-3 cursor-pointer hover:border-brand-300 hover:shadow-sm transition">
                  <div className="text-[10px] uppercase font-bold text-slate-400 mb-1">Customer address (on file)</div>
                  <div className="font-semibold break-words whitespace-normal leading-snug">{custAddr || "—"}</div>
                </div>
                <div role="button" tabIndex={0} onClick={() => setShowPickupCompare(true)} data-testid="addr-card-pickup"
                  className="bg-white border border-slate-200 rounded-xl p-3 cursor-pointer hover:border-brand-300 hover:shadow-sm transition">
                  <div className="text-[10px] uppercase font-bold text-slate-400 mb-1 flex flex-wrap items-center gap-1.5">
                    Pickup / origin
                    {shipment?.warehouse && !originMatchesCustomer && (pickupLoc?.name || shipment?.warehouse_address) && (
                      <span data-testid="pickup-location-link" className="normal-case text-[11px] font-semibold text-blue-600 underline">{pickupLoc?.name || "Pickup location"}</span>
                    )}
                    {custAddr && (
                      originMatchesCustomer
                        ? <span className="normal-case text-emerald-600 text-[10px] font-semibold">(same as customer)</span>
                        : <span className="normal-case text-amber-600 text-[10px] font-semibold">⚠ differs from customer</span>
                    )}
                  </div>
                  <div className="font-semibold break-words whitespace-normal leading-snug">{originStr || "—"}</div>
                </div>
                <div role="button" tabIndex={0} onClick={() => setAddrDetail("dest")} data-testid="addr-card-destination"
                  className="bg-white border border-slate-200 rounded-xl p-3 cursor-pointer hover:border-brand-300 hover:shadow-sm transition">
                  <div className="text-[10px] uppercase font-bold text-slate-400 mb-1">Destination address</div>
                  <div className="font-semibold break-words whitespace-normal leading-snug">{destStr || "—"}</div>
                </div>
              </div>
            );
          })()}
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="text-xs font-bold text-amber-700 uppercase mb-2">Weight & Amount Reconciliation</div>
          {shipment?.v2_actual_weight || shipment?.weight_mismatch ? (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-slate-500 text-xs">Corrected Weight</div>
                <div className="font-semibold">{shipment.v2_actual_weight} kg</div>
              </div>
              <div>
                <div className="text-slate-500 text-xs">Corrected Amount</div>
                <div className="font-semibold">₹{shipment.v2_corrected_price || "Pending"}</div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-amber-700">Not yet reconciled by carrier. This section will update once weight verification is complete.</p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-5">
          {/* LEFT — Timeline */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4">
              Latest updates ({events.length} events)
            </h3>
            {loading ? (
              <div className="py-12 flex justify-center text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
              </div>
            ) : events.length === 0 ? (
              <div className="py-12 text-center text-slate-400">
                <Package className="w-10 h-10 mx-auto mb-2 opacity-60" />
                <p className="text-sm">No events yet</p>
              </div>
            ) : (
              <>
                <TimelineList events={sortedEvents.slice(0, 4)} />
                {sortedEvents.length > 4 && (
                  <button onClick={() => setShowAllEvents(true)} data-testid="view-all-events"
                    className="mt-4 w-full py-2 text-sm font-semibold text-brand-600 border border-brand-200 rounded-lg hover:bg-brand-50">
                    View all {sortedEvents.length} events
                  </button>
                )}
              </>
            )}
          </div>

          {/* RIGHT — Action panel */}
          <div className="space-y-4">
            <ShipmentDocumentsCard shipment={shipment} onChanged={loadAll} />
            <DelhiveryLrCopies shipment={shipment} />
            <EwayBillSection shipment={shipment} onUpdated={loadAll} />

            {/* Manual scan (form opens in a popup) */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Manual update</h3>
              {is3pl ? (
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">Manual updates are disabled - this shipment is tracked automatically via Delhivery.</p>
              ) : (
                <Button onClick={() => setScanOpen(true)} variant="outline" className="w-full" data-testid="open-manual-scan"><Plus className="w-4 h-4 mr-2" /> Add manual scan</Button>
              )}
            </div>

            {/* 3PL Sync */}
            {is3pl && (
              <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
                <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                  <Truck className="w-4 h-4" /> 3PL Sync
                </h3>
                <p className="text-xs text-slate-500">
                  Last synced: {lastSync ? new Date(lastSync).toLocaleString("en-IN") : "Never"}
                </p>
                <Button onClick={sync3PL} disabled={syncing} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="sync-3pl-btn">
                  {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Sync Now
                </Button>
                <Button onClick={checkLive} variant="outline" className="w-full" data-testid="check-live-btn">
                  <Eye className="w-4 h-4 mr-2" /> Check Live Status
                </Button>
              </div>
            )}

            {/* Labels */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Labels</h3>
              <Button onClick={() => downloadLabel(null)} variant="outline" className="w-full" data-testid="download-label-btn">
                <Download className="w-4 h-4 mr-2" /> Download Label{awbs.length > 1 ? "s (merged)" : ""}
              </Button>
              {awbs.length > 1 && (
                <Button onClick={() => setShowAllAwbs(true)} variant="ghost" className="w-full justify-start text-xs" data-testid="individual-labels-btn">
                  Individual labels ({awbs.length} boxes)
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>


      {scanOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-3" onClick={() => setScanOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-5 w-full max-w-md shadow-2xl max-h-[88vh] overflow-auto space-y-3" data-testid="manual-scan-modal">
            <div className="flex items-center justify-between"><h3 className="text-base font-bold text-slate-800">Add manual scan</h3><button onClick={() => setScanOpen(false)} className="text-slate-400 hover:text-slate-700 text-lg leading-none">x</button></div>
              <Field label="Event Type">
                <select
                  value={form.event_type}
                  onChange={(e) => setForm({ ...form, event_type: e.target.value })}
                  className="w-full border border-slate-300 rounded h-10 px-2 text-sm bg-white"
                  data-testid="event-type-select"
                >
                  {EVENT_TYPES.map((t) => (
                    <option key={t} value={t}>{fmtType(t)} ({t})</option>
                  ))}
                </select>
              </Field>
              <Field label="Event Date">
                <Input
                  type="datetime-local"
                  value={form.event_date}
                  onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                  data-testid="event-date"
                />
              </Field>
              <Field label="Display Message">
                <Input
                  value={form.display_message}
                  onChange={(e) => setForm({ ...form, display_message: e.target.value })}
                  placeholder="(uses default if blank)"
                  data-testid="display-message"
                />
              </Field>
              <Field label="Internal Message">
                <Textarea
                  rows={2}
                  value={form.internal_message}
                  onChange={(e) => setForm({ ...form, internal_message: e.target.value })}
                  data-testid="internal-message"
                />
              </Field>
              <Field label="Display Location">
                <Input
                  value={form.display_location}
                  onChange={(e) => setForm({ ...form, display_location: e.target.value })}
                  placeholder="e.g. Hyderabad Hub"
                  data-testid="display-location"
                />
              </Field>
              {MIDMILE.has(form.event_type) && (
                <Field label="Vehicle Number">
                  <Input
                    value={form.vehicle_number}
                    onChange={(e) => setForm({ ...form, vehicle_number: e.target.value.toUpperCase() })}
                    placeholder="AP31AB1234"
                    data-testid="vehicle-number"
                  />
                </Field>
              )}
              {form.event_type === "DELIVERY_ATTEMPTED" && (
                <Field label="Attempt Number">
                  <Input
                    type="number"
                    min="1"
                    value={form.attempt_number}
                    onChange={(e) => setForm({ ...form, attempt_number: e.target.value })}
                    data-testid="attempt-number"
                  />
                </Field>
              )}
              {is3pl && (
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
                  Manual updates are disabled for this shipment — it's tracked automatically via Delhivery.
                </p>
              )}
              <Button
                onClick={submitEvent}
                disabled={submitting || is3pl}
                className="w-full bg-slate-800 hover:bg-slate-900 text-white disabled:opacity-50"
                data-testid="submit-event"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Add Event
              </Button>
          </div>
        </div>
      )}

      {showAllEvents && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-3" onClick={() => setShowAllEvents(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl w-full max-w-2xl shadow-2xl max-h-[88vh] flex flex-col" data-testid="timeline-modal">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between"><h3 className="text-base font-bold text-slate-800">Full timeline ({sortedEvents.length} events)</h3><Button size="sm" variant="outline" onClick={() => setShowAllEvents(false)}>Close</Button></div>
            <div className="p-5 overflow-auto"><TimelineList events={sortedEvents} /></div>
          </div>
        </div>
      )}

      {showPickupCompare && (() => {
        const o = shipment?.origin || {};
        const savedAddr = pickupLoc?.address || shipment?.warehouse_address || "";
        const norm = (t) => String(t || "").toLowerCase().replace(/[^a-z0-9]/g, "");
        const shipAddr = o.address || "";
        const sameAddr = norm(savedAddr) && (norm(shipAddr) === norm(savedAddr) || norm(shipAddr).includes(norm(savedAddr)) || norm(savedAddr).includes(norm(shipAddr)));
        const samePin = !pickupLoc?.pincode || String(pickupLoc.pincode) === String(o.pincode || "");
        const same = sameAddr && samePin;
        return (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-3" onClick={() => setShowPickupCompare(false)}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl w-full max-w-xl shadow-2xl max-h-[88vh] overflow-auto" data-testid="pickup-compare-modal">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-base font-bold text-slate-800">Pickup location: {pickupLoc?.name || "saved location"}</h3>
                <Button size="sm" variant="outline" onClick={() => setShowPickupCompare(false)}>Close</Button>
              </div>
              <div className="p-4 space-y-3 text-sm">
                <div className={`rounded-lg px-3 py-2 text-xs font-semibold border ${same ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                  {same ? "Same - the address on this order matches the saved pickup location." : `Different - ${!sameAddr ? "the address text differs" : ""}${!sameAddr && !samePin ? " and " : ""}${!samePin ? "the pincode differs" : ""}. Check before manifesting.`}
                </div>
                {(() => {
                  const dl = String(shipment?.assigned_carrier || "").startsWith("DELHIVERY");
                  const who = dl ? "Delhivery" : "Our pickup team";
                  return (
                    <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-900" data-testid="pickup-from-note">
                      <b>{who} will pick up from:</b> {dl || !savedAddr ? (savedAddr || "the saved pickup location") : shipAddr}
                      {dl ? " (the address registered with Delhivery for this pickup location)." : "."}
                      {dl && !same && <div className="mt-1 text-amber-800 font-semibold">The order's address above is different - Delhivery will not use it. Correct the order or pick the right location before manifesting.</div>}
                    </div>
                  );
                })()}
                <div>
                  <div className="text-[10px] uppercase font-bold text-slate-400">Saved pickup location address</div>
                  <div className="font-semibold">{savedAddr || "-"}</div>
                  <div className="text-xs text-slate-500">{[pickupLoc?.city, pickupLoc?.pincode].filter(Boolean).join(" - ")}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase font-bold text-slate-400">Pickup address on this order</div>
                  <div className="font-semibold">{shipAddr || "-"}</div>
                  <div className="text-xs text-slate-500">{[o.city, o.pincode].filter(Boolean).join(" - ")}</div>
                </div>
                {!same && canEditShipment(shipment) && (
                  <button onClick={() => { setShowPickupCompare(false); setEditHeaderOpen(true); }} className="text-xs font-semibold text-blue-600 underline">Edit shipment details</button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {addrDetail && (() => {
        const isDest = addrDetail === "dest";
        const d = shipment?.destination || {};
        const rows = isDest
          ? [["Name", d.name], ["Phone", d.phone], ["Address", d.address], ["City", d.city], ["State", d.state], ["Pincode", d.pincode]]
          : [["Business", customerProfile?.business_name || customerProfile?.company_name || customerProfile?.name], ["Phone", customerProfile?.phone || customerProfile?.mobile], ["Email", customerProfile?.email], ["Address on file", customerProfile?.business_address], ["GST", customerProfile?.gst_number || customerProfile?.gstin]];
        const text = rows.filter((r) => r[1]).map((r) => `${r[0]}: ${r[1]}`).join("\n");
        return (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-3" onClick={() => setAddrDetail(null)}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl w-full max-w-lg shadow-2xl max-h-[88vh] overflow-auto" data-testid="address-detail-modal">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-base font-bold text-slate-800">{isDest ? "Destination address" : "Customer address (on file)"}</h3>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { try { navigator.clipboard.writeText(text); toast.success("Copied"); } catch { /* ignore */ } }}>Copy</Button>
                  <Button size="sm" variant="outline" onClick={() => setAddrDetail(null)}>Close</Button>
                </div>
              </div>
              <div className="p-4 space-y-2 text-sm">
                {rows.filter((r) => r[1]).map(([k, v]) => (
                  <div key={k}><div className="text-[10px] uppercase font-bold text-slate-400">{k}</div><div className="font-semibold break-words">{v}</div></div>
                ))}
                {rows.every((r) => !r[1]) && <p className="text-slate-400">No details on file.</p>}
              </div>
            </div>
          </div>
        );
      })()}

      {showAllAwbs && <AwbsModal awbs={awbs} onClose={() => setShowAllAwbs(false)} onLabel={downloadLabel} />}

      {liveStatus && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setLiveStatus(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-5 w-full max-w-lg shadow-2xl max-h-[80vh] overflow-auto" data-testid="live-status-modal">
            <h3 className="text-lg font-bold text-slate-800 mb-3">Live Delhivery Status</h3>
            <pre className="bg-slate-50 border border-slate-200 rounded p-3 text-xs overflow-auto">{JSON.stringify(liveStatus, null, 2)}</pre>
            <div className="flex justify-end mt-3">
              <Button onClick={() => setLiveStatus(null)} variant="outline">Close</Button>
            </div>
          </div>
        </div>
      )}

      <DelhiveryTransitModal awb={awb} open={showTransit} onClose={() => setShowTransit(false)} />
    </DashboardLayout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold text-slate-600 uppercase mb-1">{label}</label>
      {children}
    </div>
  );
}


function EwayBillSection({ shipment, onUpdated }) {
  const [info, setInfo] = useState(null);
  const [val, setVal] = useState("");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const sid = shipment?.id || shipment?._id;

  const load = useCallback(async () => {
    if (!sid) return;
    try {
      const r = await api.get(`/v2/shipments/${sid}/eway-bill`);
      setInfo(r.data);
    } catch { /* not available */ }
  }, [sid]);

  useEffect(() => { load(); }, [load]);

  if (!info) return null;
  if (!info.eway_bill_required) return null;

  const save = async () => {
    if (!/^\d{12}$/.test((val || "").trim())) {
      toast.error("Eway Bill must be exactly 12 digits");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/v2/shipments/${sid}/eway-bill`, { eway_bill_number: val.trim() });
      toast.success("Eway Bill verified ✓");
      setEditing(false);
      setVal("");
      await load();
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  const masked = info.eway_bill_number
    ? `XXXX-XXXX-${info.eway_bill_number.slice(-4)}`
    : "";

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="eway-bill-section">
      <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Eway Bill</h3>

      {!info.eway_bill_verified || editing ? (
        <>
          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800" data-testid="eway-bill-required-banner">
            Eway Bill required (declared value &gt; ₹50,000). Pickup will be blocked until added.
          </div>
          <Input
            value={val}
            onChange={(e) => setVal(e.target.value.replace(/\D/g, "").slice(0, 12))}
            placeholder="12-digit Eway Bill"
            data-testid="eway-bill-input"
          />
          <Button
            onClick={save}
            disabled={busy || val.length !== 12}
            data-testid="eway-bill-save"
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            Save Eway Bill
          </Button>
          {editing && (
            <button onClick={() => { setEditing(false); setVal(""); }} className="text-xs text-slate-500 hover:text-slate-800">
              Cancel
            </button>
          )}
        </>
      ) : (
        <>
          <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs text-emerald-800 flex items-center justify-between" data-testid="eway-bill-verified-banner">
            <span>Eway Bill verified ✓</span>
            <span className="font-mono">{masked}</span>
          </div>
          <Button
            variant="outline"
            onClick={() => setEditing(true)}
            data-testid="eway-bill-update"
            className="w-full"
          >
            Update
          </Button>
        </>
      )}
    </div>
  );
}


const inr = (v) => (v === null || v === undefined || v === "" ? "—" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

function DocRow({ title, rows, hasFile, filename, onView, onDownload, onUpload, uploadLabel, busy }) {
  const ref = useRef(null);
  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`doc-row-${title.toLowerCase().replace(/\W+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><FileText className="w-4 h-4 text-slate-500" />{title}</span>
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${hasFile ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
          {hasFile ? "File attached" : "No file"}
        </span>
      </div>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        {rows.map(([k, v]) => (
          <div key={k} className="min-w-0"><dt className="text-slate-500">{k}</dt><dd className="font-semibold text-slate-800 font-mono break-all">{v || "—"}</dd></div>
        ))}
      </dl>
      {hasFile && filename && <p className="text-[11px] text-slate-500 truncate" title={filename}>{filename}</p>}
      <div className="flex gap-2">
        {hasFile && (
          <>
            <Button size="sm" variant="outline" className="flex-1" onClick={onView} disabled={busy} data-testid={`${title}-view`}>
              <Eye className="w-3.5 h-3.5 mr-1" /> View
            </Button>
            <Button size="sm" variant="outline" className="flex-1" onClick={onDownload} disabled={busy} data-testid={`${title}-download`}>
              <Download className="w-3.5 h-3.5 mr-1" /> Download
            </Button>
          </>
        )}
        {onUpload && (
          <>
            <Button size="sm" variant={hasFile ? "ghost" : "outline"} className={hasFile ? "" : "flex-1"} onClick={() => ref.current?.click()} disabled={busy}>
              <Upload className="w-3.5 h-3.5 mr-1" /> {hasFile ? "Replace" : uploadLabel}
            </Button>
            <input ref={ref} type="file" accept="application/pdf,image/*" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onUpload(f); }} />
          </>
        )}
      </div>
    </div>
  );
}

function ShipmentDocumentsCard({ shipment, onChanged }) {
  const sid = shipment?.id || shipment?._id;
  const [sum, setSum] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [fix, setFix] = useState(null);   // must stay above the early return below (rules of hooks)

  const load = useCallback(async () => {
    if (!sid) return;
    try { setSum((await api.get(`/v2/shipment-docs/${sid}/summary`)).data); } catch { /* ignore */ }
  }, [sid]);
  useEffect(() => { load(); }, [load]);

  if (!sum) return null;
  const { invoice: inv, eway_bill: ew } = sum;

  const open = async (doc, download) => {
    setBusy(true);
    try {
      const r = await api.get(`/v2/shipment-docs/${sid}/${doc}/link`, { params: { download } });
      if (download) {
        const a = document.createElement("a"); a.href = r.data.url; a.download = r.data.filename || doc; document.body.appendChild(a); a.click(); a.remove();
      } else window.open(r.data.url, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open the document"); }
    setBusy(false);
  };
  // Fix a mistyped invoice / e-way bill number at any stage (also after assignment). Audit-logged on the server.
  const startFix = () => setFix({ invoice_number: inv.number || "", eway_bill_number: ew.number || "" });
  const saveFix = async () => {
    const e = (fix.eway_bill_number || "").trim();
    if (e && !/^\d{12}$/.test(e)) { toast.error("E-way bill number must be exactly 12 digits"); return; }
    setBusy(true);
    try {
      const r = await api.put(`/v2/shipment-docs/${sid}/booking-docs`, { invoice_number: fix.invoice_number, eway_bill_number: e });
      toast.success(r.data?.saved ? "Details corrected" : "Nothing changed");
      if (r.data?.carrier_note) toast.message(r.data.carrier_note);
      setFix(null); await load(); onChanged?.();
    } catch (er) { toast.error(er.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };
  const upload = async (path, f) => {
    if (f.size > 8 * 1024 * 1024) { toast.error("File must be under 8 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post(path, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded"); await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="shipment-documents-card">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Invoice &amp; E-way Bill</h3>
        <div className="flex items-center gap-3">
          {!fix && <button onClick={startFix} data-testid="edit-doc-numbers-btn" className="text-xs font-semibold text-brand-600 hover:underline">Correct invoice / e-way no.</button>}
          {canEditShipment(shipment) && <button onClick={() => setEditing(true)} data-testid="edit-shipment-btn" className="text-xs font-semibold text-brand-600 hover:underline">Edit shipment details</button>}
        </div>
      </div>
      {fix && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="doc-numbers-form">
          <p className="text-xs text-amber-900">Fix a mistyped number. Every change is logged with your name. If the shipment is already with the carrier, tell them the corrected number.</p>
          <div className="grid sm:grid-cols-2 gap-2">
            <div><label className="block text-[11px] font-semibold text-slate-600 uppercase mb-1">Invoice no.</label>
              <input value={fix.invoice_number} maxLength={40} onChange={(e) => setFix({ ...fix, invoice_number: e.target.value })} data-testid="fix-invoice-no" className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm" /></div>
            <div><label className="block text-[11px] font-semibold text-slate-600 uppercase mb-1">E-way bill no. (12 digits)</label>
              <input value={fix.eway_bill_number} inputMode="numeric" maxLength={12} onChange={(e) => setFix({ ...fix, eway_bill_number: e.target.value.replace(/\D/g, "") })} data-testid="fix-eway-no" className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm font-mono" /></div>
          </div>
          <div className="flex gap-2">
            <button disabled={busy} onClick={saveFix} data-testid="fix-save" className="px-3 py-1.5 bg-brand-600 text-white rounded-md text-xs font-semibold disabled:opacity-50">Save</button>
            <button disabled={busy} onClick={() => setFix(null)} className="px-3 py-1.5 border border-slate-300 rounded-md text-xs">Cancel</button>
          </div>
        </div>
      )}
      {editing && <ShipmentEditModal shipmentId={sid} onClose={() => setEditing(false)} onSaved={() => { load(); onChanged?.(); }} />}
      <DocRow title="Invoice" hasFile={inv.has_file} filename={inv.filename} busy={busy}
        rows={[["Invoice no.", inv.number], ["Sendtoo invoice", inv.sendtoo_number], ["Date", fmtDate(inv.date)], ["Goods value", inr(inv.declared_value)]]}
        onView={() => open("invoice", false)} onDownload={() => open("invoice", true)}
        onUpload={(f) => upload(`/shipments/${sid}/invoice`, f)} uploadLabel="Upload invoice" />
      <DocRow title="E-way Bill" hasFile={ew.has_file} filename={ew.filename} busy={busy}
        rows={[["E-way bill no.", ew.number], ["Status", ew.number ? (ew.verified ? "Verified ✓" : "Entered") : (ew.required ? "Required - missing" : "Not required")], ["Goods value", inr(ew.declared_value)], ["Uploaded", fmtDate(ew.uploaded_at)]]}
        onView={() => open("eway-bill", false)} onDownload={() => open("eway-bill", true)}
        onUpload={(f) => upload(`/v2/shipment-docs/${sid}/eway-bill`, f)} uploadLabel="Upload e-way PDF" />
    </div>
  );
}


function DelhiveryLrCopies({ shipment }) {
  const sid = shipment?.id || shipment?._id;
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const load = useCallback(async () => {
    if (!sid) return;
    try { setData((await api.get(`/v2/delhivery-docs/${sid}`)).data); } catch { /* ignore */ }
  }, [sid]);
  useEffect(() => { load(); }, [load]);
  if (!data || (shipment.assigned_carrier !== "DELHIVERY_B2B" && !(data.copies || []).length)) return null;

  const fetchCopies = async () => {
    setBusy(true); setReport(null);
    try {
      const r = await api.post(`/v2/delhivery-docs/${sid}/fetch`, {});
      setReport(r.data);
      if (r.data.fetched) toast.success(`Fetched ${r.data.fetched} Delhivery copy(ies)`);
      else toast.error("Delhivery did not return any copy - see details below");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not fetch from Delhivery"); }
    setBusy(false);
  };
  const open = async (c, download) => {
    try {
      const r = await api.get(`/v2/delhivery-docs/${sid}/${c.kind}/${c.size}/link`, { params: { download } });
      if (download) { const a = document.createElement("a"); a.href = r.data.url; a.download = r.data.filename; document.body.appendChild(a); a.click(); a.remove(); }
      else window.open(r.data.url, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open the copy"); }
  };
  const kb = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="delhivery-lr-copies">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading font-bold text-sm text-slate-900">Delhivery LR copies{data.lr_number ? ` - ${data.lr_number}` : ""}</h3>
        {data.can_fetch && (
          <Button size="sm" variant="outline" disabled={busy} onClick={fetchCopies} data-testid="fetch-delhivery-lr">
            {busy ? "Fetching..." : (data.copies || []).length ? "Re-fetch" : "Fetch from Delhivery"}
          </Button>
        )}
      </div>
      {!data.can_fetch && !(data.copies || []).length && <p className="text-xs text-slate-500">Available once the Delhivery B2B LR number is issued.</p>}
      <div className="space-y-1.5">
        {(data.copies || []).map((c) => (
          <div key={`${c.kind}-${c.size}`} className="flex items-center justify-between text-xs border border-slate-100 rounded-lg px-2 py-1.5">
            <div><span className="font-semibold">{c.kind_label || c.kind}</span> <span className="text-slate-500">| {c.size_label || c.size} | {c.pages ? `${c.pages} page${c.pages > 1 ? "s" : ""} | ` : ""}{kb(c.size_bytes)}</span></div>
            <div className="flex gap-2">
              <button className="text-brand-600 font-semibold hover:underline" onClick={() => open(c, false)}>View</button>
              <button className="text-brand-600 font-semibold hover:underline" onClick={() => open(c, true)}>Download</button>
            </div>
          </div>
        ))}
      </div>
      {report && report.failed > 0 && (
        <details className="mt-2 text-[11px] text-slate-600" open={!report.fetched}>
          <summary className="cursor-pointer font-semibold text-amber-700">{report.failed} size(s) not returned by Delhivery</summary>
          {report.results.filter((r) => !r.ok).slice(0, 3).map((r) => (
            <div key={`${r.kind}-${r.size}`} className="mt-1"><b>{r.kind}/{r.size}</b>{(r.attempts || []).map((a, i) => <div key={i} className="font-mono break-all">{a.status ?? "-"} {a.template} - {a.note}</div>)}</div>
          ))}
        </details>
      )}
    </div>
  );
}


function TimelineList({ events }) {
  return (
    <div className="relative">
      <div className="absolute left-3 top-2 bottom-2 w-px bg-slate-200" />
      <div className="space-y-4">
        {events.map((ev, i) => (
                      <div key={ev.event_id || i} className="relative pl-9" data-testid={`event-${ev.event_type}`}>
                        <div
                          className={`absolute left-1 top-1.5 w-5 h-5 rounded-full border-2 ${
                            i === 0 ? "bg-slate-800 border-slate-800 animate-pulse" : "bg-emerald-500 border-emerald-500"
                          }`}
                        />
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="font-semibold text-slate-800 text-sm">{fmtType(ev.event_type)}</div>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${SOURCE_BADGE[ev.source] || SOURCE_BADGE.MANUAL}`}>
                            {ev.source}
                          </span>
                        </div>
                        <div className="text-sm text-slate-600 mt-0.5">{ev.display_message}</div>
                        {ev.internal_message && ev.internal_message !== ev.display_message && (
                          <div className="text-xs text-slate-500 italic mt-0.5">
                            <span className="font-semibold not-italic">Internal: </span>
                            {ev.internal_message}
                          </div>
                        )}
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500 mt-1.5">
                          {ev.display_location && <span>📍 {ev.display_location}</span>}
                          {ev.vehicle_number && <span>🚚 {ev.vehicle_number}</span>}
                          {ev.attempt_number != null && <span>Attempt #{ev.attempt_number}</span>}
                          {ev.scanned_by && (
                            <span>
                              By {ev.scanned_by_type || "—"}
                            </span>
                          )}
                          <span>{ev.event_date ? new Date(ev.event_date).toLocaleString("en-IN") : "—"}</span>
                        </div>
                      </div>
        ))}
      </div>
    </div>
  );
}

function AwbsModal({ awbs, onClose, onLabel }) {
  const [q, setQ] = useState("");
  const list = awbs.map((a, i) => ({ a, n: i + 1 })).filter((x) => !q || x.a.includes(q.trim()) || String(x.n) === q.trim());
  const copy = (t) => { try { navigator.clipboard.writeText(t); toast.success("Copied"); } catch { /* ignore */ } };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-3" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl w-full max-w-2xl shadow-2xl max-h-[85vh] flex flex-col" data-testid="awbs-modal">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3">
          <h3 className="text-base font-bold text-slate-800">All AWBs ({awbs.length} boxes)</h3>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => copy(awbs.join("\n"))}>Copy all</Button>
            <Button size="sm" variant="outline" onClick={onClose}>Close</Button>
          </div>
        </div>
        <div className="p-3 border-b border-slate-100">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search AWB or box number" />
        </div>
        <div className="overflow-auto p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {list.map(({ a, n }) => (
            <div key={a} className="flex items-center justify-between border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs">
              <span><span className="text-slate-400">Box {n}</span> <span className="font-mono font-semibold">{a}</span></span>
              <span className="flex gap-2">
                <button className="text-brand-600 hover:underline" onClick={() => copy(a)}>Copy</button>
                {onLabel && <button className="text-brand-600 hover:underline" onClick={() => onLabel(a)}>Label</button>}
              </span>
            </div>
          ))}
          {list.length === 0 && <p className="text-sm text-slate-400 col-span-2 text-center py-6">No match</p>}
        </div>
      </div>
    </div>
  );
}
